import { colorFromCss } from '@snapbrains/snaputils/src/legacy';
import { deriveColors } from '@snapbrains/snaputils/src/color';
import { parseQueryString } from '@snapbrains/snaputils/src/query_string';
import { getRandomInt } from '@snapbrains/snaputils/src/numbers';
import { camelize } from '@snapbrains/snaputils/src/strings';

import { bestMatchSort } from '../defines';

// This file contains methods for default settings and extracting settings from
// a webpage.
const defaultDarkColor = '#2C3E50';
const defaultColors = deriveColors(defaultDarkColor);

export const simpleWidth = 160;
export const simpleHeight = 110;
export const simpleFontSize = 14;
export const simpleBorderRadius = 0;

export const emptySearchFilters = {
  brand: '',
  category: '',
  customCategory: '',
  subCategory: '',
  merchantIds: [],
  onSaleOnly: false,
  onSaleLevel: null,
  minPrice: null,
  maxPrice: null,
  department: '',
  merchantCategoryIds: [],
};

export const defaultSearchArgs = {
  query: null,
  page: 0,
  sortOrder: bestMatchSort,
  resultCount: 20,
  avantlinkAffiliateId: '',
  avantlinkWebsiteId: '',
  ...emptySearchFilters,
};

export const defaultSettings = {
  // Required options
  element: 'snapsearcher-container',

  // UI options
  resultType: 'flex',
  darkColor: defaultDarkColor,
  lightColor: defaultColors.lightColor,
  highLightColor: defaultColors.highLightColor,
  buttonColor: defaultColors.buttonColor,
  saleTagColor: '#c0392b', // pomegranate
  borderColor: '#95A5A6', // concrete
  textColor: '#7F8C8D', // asbestos
  backgroundColor: '',
  showMoreResults: true,
  showMoreResultsText: 'Load More',
  placeholder: '',
  openNewWindow: true,
};

// This will ensure default values as well as convert all keys to camelCase.
// The conversion to camelCase is required to handle legacy settings.
// This function takes the settings provided from the client page and turns them into a settings
// object that can be passed into the GlobalStyles and Search components
export function configureSettings(currentSettings) {
  // Setup default settings
  const settings = defaultSettings;
  settings.darkColor = colorFromCss(currentSettings.css)
    || currentSettings.color
    || settings.darkColor;
  const colors = deriveColors(settings.darkColor);

  settings.lightColor = colors.lightColor;
  settings.highLightColor = colors.highLightColor;
  settings.buttonColor = colors.buttonColor;

  // Override defaults with values provided in init
  Object.keys(currentSettings).forEach((currentKey) => {
    settings[camelize(currentKey)] = currentSettings[currentKey];
  });

  // SnapDealist Legacy
  if (settings.useQueryParamName) {settings.queryParam = settings.useQueryParamName;}
  if (settings.useQuery) {settings.defaultQuery = settings.useQuery;}

  // Legacy installs of SnapSearcher and SnapDealist will have affiliate_id and website_id. The new
  // snapav requires avantlinkAffiliateId and avantlinkWebsiteId.
  settings.avantlinkAffiliateId = settings.avantlinkAffiliateId
    || settings.affiliateId
    || settings.affiliate_id;

  settings.avantlinkWebsiteId = settings.avantlinkWebsiteId
    || settings.websiteId
    || settings.website_id;

  return settings;
}

// Given the settings object from configureSettings this method will attempt to
// generate a query based on the values in settings.
export function getQueryFromSettings(settings) {
  let query = '';
  const queryString = parseQueryString();

  if (settings.queryParam
    && queryString.get(settings.queryParam)
    && queryString.get(settings.queryParam).length > 0) {
    query = queryString.get(settings.queryParam);
  } else if (settings.defaultQuery) {
    const possibleQueries = settings.defaultQuery.split('|');
    query = possibleQueries[getRandomInt(0, possibleQueries.length - 1)];
  } else if (settings.useSelector) {
    const queries = [];
    const els = document.querySelectorAll(settings.useSelector);
    for (let i = 0; i < els.length; i += 1) {
      queries.push(els[i].textContent.trim());
    }
    query = queries.join(' AND ');
  } else if (settings.queryType === 'useUrl') {
    const path = window.location.pathname.split('.')[0];
    const parts = path.split('/').filter((x) => !!x);
    query = parts[parts.length - 1] || '';
    query = query.replace(/-/g, ' ').replace(/_/g, ' ');
  }

  return query;
}

// Given the settings object from configureSettings this method extracts the
// search specific settings that can them be provided to SearchContextProvider
export function searchSettings(settings) {
  const searchArgs = {
    ...defaultSearchArgs,
  };

  // Allow for a custom search API endpoint. Handy if the search needs to be proxied.
  if (settings.searchUrl) {
    searchArgs.searchUrl = settings.searchUrl;
  }

  // Allow passing headers that can be sent to the search API endpoint
  if (settings.searchHeaders) {
    searchArgs.searchHeaders = settings.searchHeaders;
  }

  if (settings.page || settings.page === 0) {
    searchArgs.page = settings.page;
  }

  if (settings.query) {
    searchArgs.query = settings.query;
  } else {
    searchArgs.query = getQueryFromSettings(settings);
  }

  if (settings.sortOrder) {
    searchArgs.sortOrder = settings.sortOrder;
  } else if (settings.searchResultsSortOrder) {
    searchArgs.sortOrder = settings.searchResultsSortOrder;
  } else if (settings.sortHighestCommissionFirst) {
    searchArgs.sortHighestCommissionFirst = settings.sortHighestCommissionFirst;
  }

  if (settings.merchantIds && settings.merchantIds.length > 0) {
    searchArgs.merchantIds = settings.merchantIds;
  }

  if (settings.merchantCategoryIds && settings.merchantCategoryIds.length > 0) {
    searchArgs.merchantCategoryIds = settings.merchantCategoryIds;
  }

  if (settings.category) {
    searchArgs.category = settings.category;
  }

  if (settings.subCategory) {
    searchArgs.subCategory = settings.subCategory;
  }

  if (settings.brand) {
    searchArgs.brand = settings.brand;
  }

  if (settings.department) {
    searchArgs.department = settings.department;
  }

  if (settings.resultCount) {
    searchArgs.resultCount = parseInt(settings.resultCount, 10);
  }

  searchArgs.avantlinkAffiliateId = settings.avantlinkAffiliateId;
  searchArgs.avantlinkWebsiteId = settings.avantlinkWebsiteId;

  if (settings.onSaleOnly) {
    searchArgs.onSaleOnly = settings.onSaleOnly;
  } else if (settings.searchOnSaleOnly) {
    searchArgs.onSaleOnly = settings.searchOnSaleOnly;
  }

  if (settings.onSaleLevel) {
    searchArgs.onSaleLevel = settings.onSaleLevel;
  } else if (settings.searchOnSaleLevel) {
    searchArgs.onSaleLevel = settings.searchOnSaleLevel;
  }

  searchArgs.minPrice = settings.searchPriceMinimum || settings.minPrice || 0;
  searchArgs.maxPrice = settings.searchPriceMaximum || settings.maxPrice || 10000;

  return searchArgs;
}
